import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import moment from 'moment';
import { toast } from 'react-toastify';
import { GetSleepScreen } from '../../libs/GraphQL';
import SleepScreenVideoPreview from './SleepScreenVideoPreview';
import styles from './SleepScreenDetail.module.css';

const formatDate = (iso, by) => {
  if (!iso) return '—';
  const m = moment(iso);
  if (!m.isValid()) return '—';
  const date = m.format('MMM D, YYYY');
  return by ? `${date} · ${by}` : date;
};

const SleepScreenDetail = () => {
  const { sleepScreenId } = useParams();
  const [entry, setEntry] = useState(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    let cancelled = false;
    const fetchOne = async () => {
      setStatus('loading');
      try {
        const result = await GetSleepScreen(sleepScreenId);
        if (cancelled) return;
        if (!result) {
          setEntry(null);
          setStatus('notFound');
        } else {
          setEntry(result);
          setStatus('loaded');
        }
      } catch (err) {
        if (cancelled) return;
        console.error('getSleepScreen failed:', err);
        toast.error('Failed to load sleep screen.');
        setStatus('error');
      }
    };
    fetchOne();
    return () => {
      cancelled = true;
    };
  }, [sleepScreenId]);

  if (status === 'loading') {
    return (
      <main className={styles.container}>
        <div className={styles.breadcrumb}>
          <Link to="/sleep-screens">Library</Link>
        </div>
        <div className={styles.stateBlock}>Loading…</div>
      </main>
    );
  }

  if (status === 'notFound') {
    return (
      <main className={styles.container}>
        <div className={styles.breadcrumb}>
          <Link to="/sleep-screens">Library</Link> &gt; not found
        </div>
        <div className={styles.stateBlock}>
          No sleep screen with id <code>{sleepScreenId}</code> exists.
        </div>
      </main>
    );
  }

  if (status === 'error' || !entry) {
    return (
      <main className={styles.container}>
        <div className={styles.breadcrumb}>
          <Link to="/sleep-screens">Library</Link>
        </div>
        <div className={styles.stateBlock}>Couldn’t load this entry.</div>
      </main>
    );
  }

  return (
    <main className={styles.container}>
      <div className={styles.breadcrumb}>
        <Link to="/sleep-screens">Library</Link> &gt;{' '}
        {entry.displayName || entry.sleepScreenId}
      </div>

      <h1>Library Entry</h1>

      <section className={styles.topSection}>
        <SleepScreenVideoPreview url={entry.videoUrl} />
        <div className={styles.topMeta}>
          <div className={styles.displayName}>{entry.displayName || '—'}</div>
          <div className={styles.id}>{entry.sleepScreenId}</div>
        </div>
      </section>

      <section className={styles.detailsCard}>
        <h2>Details</h2>
        <dl className={styles.detailsGrid}>
          <dt>Name</dt>
          <dd>{entry.displayName || '—'}</dd>
          <dt>ID</dt>
          <dd className={styles.mono}>{entry.sleepScreenId}</dd>
          <dt>Description</dt>
          <dd>{entry.description || '—'}</dd>
          <dt>Video URL</dt>
          <dd className={styles.mono}>
            {entry.videoUrl ? (
              <a
                href={entry.videoUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {entry.videoUrl}
              </a>
            ) : (
              '—'
            )}
          </dd>
          <dt>Created on</dt>
          <dd>{formatDate(entry.createdAt, entry.createdBy)}</dd>
          <dt>Last edited on</dt>
          <dd>{formatDate(entry.updatedAt)}</dd>
        </dl>
      </section>
    </main>
  );
};

export default SleepScreenDetail;
